import { ReactNode, Ref } from 'react';
import {
  Checkbox as ChakraCheckbox,
  CheckboxProps as ChakraCheckboxProps,
  CheckboxIconProps,
  forwardRef,
  FormControl,
  FormHelperText,
  FormErrorMessage,
  FormControlProps,
} from '@chakra-ui/react';
import Icon from '../../Icon/Icon';

type CheckboxInputProps = ChakraCheckboxProps['inputProps'] & {
  'data-testid'?: string;
};

export interface CheckboxProps
  extends Omit<FormControlProps, 'icon' | 'iconColor' | 'iconSize' | 'inputProps' | 'value' | 'onChange'> {
  inputProps?: CheckboxInputProps;
  helperText?: ReactNode;
  errorText?: string;
  value?: ChakraCheckboxProps['value'];
  inputRef?: Ref<HTMLInputElement>;
  isIndeterminate?: ChakraCheckboxProps['isIndeterminate'];
  isChecked?: ChakraCheckboxProps['isChecked'];
  isDisabled?: ChakraCheckboxProps['isDisabled'];
  onChange?: ChakraCheckboxProps['onChange'];
}

const CustomIcon = (props: CheckboxIconProps) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { __css, isChecked, isIndeterminate } = props;
  if (!isChecked) {
    return null;
  }
  return <Icon __css={__css} name={isIndeterminate ? 'Minus' : 'Check'} />;
};

/**
 * Checkbox component is used in forms when a user needs to select multiple values from several options.
 */
const Checkbox = forwardRef<CheckboxProps, 'input'>((props, ref) => {
  const {
    'aria-label': ariaLabel,
    helperText,
    isInvalid,
    errorText,
    autoFocus,
    name,
    onBlur,
    role,
    value,
    inputRef,
    inputProps,
    children,
    isIndeterminate,
    isChecked,
    isDisabled,
    onChange,
    id,
    ...rest
  } = props;

  const formControlProps = {
    isInvalid: isInvalid || !!errorText,
    ref,
    ...rest,
  };

  const checkboxProps: ChakraCheckboxProps = {
    'aria-label': ariaLabel || inputProps?.['aria-label'],
    autoFocus,
    children,
    inputProps,
    name,
    onBlur,
    role,
    value,
    isIndeterminate,
    isChecked,
    isDisabled,
    onChange,
    id,
  };

  return (
    <FormControl {...formControlProps}>
      <ChakraCheckbox icon={<CustomIcon />} {...checkboxProps} ref={inputRef} />
      {errorText && (
        <FormErrorMessage marginLeft="32" marginTop="0" as="p">
          {errorText}
        </FormErrorMessage>
      )}
      {!errorText && helperText && (
        <FormHelperText marginLeft="32" marginTop="0" as="p">
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
});

export default Checkbox;
